/**
 * Estado da aplicacao: conexao com o servidor, conversa, voz e preferencias
 * locais. O contrato vive em `appTypes.ts` — aqui fica so a implementacao.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_CAM_QUALITY,
  DEFAULT_CHANNEL_ID,
  DEFAULT_SHARE_QUALITY,
  MAX_AVATAR_BYTES,
  MAX_MESSAGE_LENGTH,
  MAX_UPLOAD_BYTES,
  OWNER_ROLE_ID,
  QUALITY_PRESETS,
  HISTORY_PAGE,
  TYPING_THROTTLE_MS,
  TYPING_TTL_MS,
  channelKey as makeChannelKey,
  dmKey,
  dmPartner,
  extractHandles,
  formatBytes,
  isAudio,
  isImage,
  isTimedOut,
  isVideo,
  normalizeHandle,
  SESSION_STORAGE_KEY,
} from "@shared/protocol";
import type {
  Attachment,
  FriendLink,
  FriendProfile,
  FriendState,
  Message,
  PermissionId,
  Role,
  ServerSettings,
  Snapshot,
  TextChannel,
  User,
  VoiceChannel,
  VoiceState,
} from "@shared/types";
import { connectSocket, type LfrSocket } from "@/net/socket";
import { endSessionOnServer } from "@/lib/account";
import { VoiceMesh } from "@/media/webrtcMesh";
import type {
  AdminOuvintes,
  AppActions,
  AppContextValue,
  AppState,
  NotificationPrefs,
  PendingUpload,
  RemoteMedia,
  SideTab,
  TypingEntry,
} from "@/state/appTypes";

export type {
  AppState,
  AppActions,
  AppContextValue,
  SettingsTab,
  SideTab,
  RemoteMedia,
  PendingUpload,
  TypingEntry,
  SearchState,
  NotificationPrefs,
  AudioDevices,
} from "./appTypes";

const AppContext = createContext<AppContextValue | null>(null);

/** Vale so enquanto o snapshot do servidor nao chega. */
const EMPTY_SETTINGS: ServerSettings = {
  name: "LFRCORD",
  description: "",
  icon: "L",
  iconUrl: null,
  accent: "#1F4FD8",
};

/** Tempo que o aviso de expulsao fica na tela antes de voltar para a entrada. */
const KICK_EXIT_MS = 6_000;

/** Intervalo da limpeza local do aviso de digitacao. */
const TYPING_SWEEP_MS = 1_000;

/** Sem nenhuma interacao por este tempo, o status vira `ausente` sozinho. */
const AUTO_AWAY_MS = 5 * 60 * 1_000;

/** De quanto em quanto tempo a inatividade e conferida. */
const AWAY_CHECK_MS = 20_000;

/** Onde as preferencias de aviso ficam guardadas no navegador. */
const NOTIFY_PREFS_KEY = "lfrcord.avisos";

/** "Ignorar" e só uma preferência local (ao contrário de "bloquear", que o servidor guarda). */
const IGNORED_USERS_KEY = "lfrcord.ignorados";

/** Tamanho do trecho da mensagem mostrado no aviso do navegador. */
const NOTIFY_BODY_LIMIT = 140;

/** Qualquer um destes eventos conta como "o membro esta aqui". */
const ACTIVITY_EVENTS: string[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "wheel",
  "touchstart",
  "focus",
];

const INITIAL: AppState = {
  phase: "connecting",
  error: null,
  notice: null,
  kicked: null,

  me: null,
  peerId: "",
  users: [],
  roles: [],
  channels: [],
  voiceChannels: [],
  settings: EMPTY_SETTINGS,
  voiceStates: [],
  unread: {},
  iceServers: [],
  maxUploadBytes: MAX_UPLOAD_BYTES,
  auditLog: null,
  auditLogLoading: false,
  platformFounder: false,
  painelAberto: false,
  banimento: null,
  avisoDaPlataforma: null,
  messages: {},
  historyHasMore: {},
  historyLoading: {},
  blocked: [],
  ignored: [],
  friends: [],
  friendProfiles: {},
  conversations: [],
  // Assume disponível até o servidor dizer o contrário: o evento `friends`
  // chega logo depois do `ready`, e supor indisponível faria a barra de
  // diretas piscar no modo antigo (todos os membros) a cada conexão.
  friendsAvailable: true,
  servers: [],
  activeServerId: null,
  serversAvailable: false,
  inviteCode: null,
  serverMemberIds: [],

  tab: "canais",
  activeKey: makeChannelKey(DEFAULT_CHANNEL_ID),
  lastTextKey: makeChannelKey(DEFAULT_CHANNEL_ID),
  membersOpen: true,
  settingsOpen: false,
  settingsTab: "geral",
  profileEditorOpen: false,
  roleSel: "",
  profile: null,
  drafts: {},
  saved: false,

  replyTo: null,
  editing: null,
  uploads: [],
  typing: {},
  search: { open: false, query: "", loading: false, results: [] },
  reactionPickerFor: null,

  voiceId: null,
  muted: false,
  deafened: false,
  cam: false,
  sharing: false,
  shareQuality: DEFAULT_SHARE_QUALITY,
  camQuality: DEFAULT_CAM_QUALITY,
  contentHint: "motion",
  pushToTalk: false,
  noiseSuppression: true,
  volumes: {},
  devices: { inputs: [], outputs: [], inputId: null, outputId: null },
  local: { mic: null, cam: null, screen: null },
  remote: {},
  speaking: {},
  stats: {},
  focus: null,
  shareModalOpen: false,
  shareSystemAudio: false,
  lastShareResult: null,
  contextMenu: null,

  reconnecting: false,
  switcherOpen: false,
  notifications: { enabled: true, sound: true, permission: "default" },
  newSince: {},
  autoAway: false,
};

const emptyRemote = (): RemoteMedia => ({ mic: null, cam: null, screen: null });

/* -------------------------------------------------------------------- */
/* Utilidades puras                                                      */
/* -------------------------------------------------------------------- */

function newId(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

/** O foco esta num campo de texto? Usado para nao roubar a barra de espaco. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable;
}

/** Le o `Attachment` devolvido pelo `/api/upload`, sem confiar no formato. */
function parseAttachment(raw: string): Attachment | null {
  let data: unknown;
  try {
    data = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  const record = asRecord(data);
  if (!record) return null;
  const { id, name, mime, size, url } = record;
  if (
    typeof id !== "string" ||
    typeof name !== "string" ||
    typeof mime !== "string" ||
    typeof size !== "number" ||
    typeof url !== "string"
  ) {
    return null;
  }
  const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
  return {
    id,
    name,
    mime,
    size,
    url,
    width: num(record.width),
    height: num(record.height),
    durationMs: num(record.durationMs),
  };
}

/** Mensagem de erro do upload: usa a do servidor quando ela existe. */
function uploadErrorMessage(status: number, raw: string): string {
  let data: unknown;
  try {
    data = JSON.parse(raw) as unknown;
  } catch {
    data = null;
  }
  const record = asRecord(data);
  const fromServer = record
    ? [record.message, record.error].find(value => typeof value === "string" && value.length > 0)
    : undefined;
  if (typeof fromServer === "string") return fromServer;
  if (status === 401) return "Sua sessão expirou. Entre de novo para enviar arquivos.";
  if (status === 403) return "Você não tem permissão para enviar arquivos.";
  if (status === 413) return "O arquivo passa do limite aceito pelo servidor.";
  if (status === 415) return "Este tipo de arquivo não é aceito.";
  return `Não foi possível enviar o arquivo (erro ${status}).`;
}

type Measured = { width: number | null; height: number | null; durationMs: number | null };

const NOT_MEASURED: Measured = { width: null, height: null, durationMs: null };

/**
 * Mede imagem, video ou audio no proprio navegador — o servidor nao abre o
 * arquivo. Qualquer falha devolve medidas vazias, nunca um valor inventado.
 */
function measureFile(file: File): Promise<Measured> {
  const mime = file.type;
  if (!isImage(mime) && !isVideo(mime) && !isAudio(mime)) return Promise.resolve(NOT_MEASURED);
  return new Promise<Measured>(resolve => {
    const url = URL.createObjectURL(file);
    let done = false;
    const finish = (value: Measured) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(NOT_MEASURED), 5_000);

    if (isImage(mime)) {
      const image = new Image();
      image.onload = () =>
        finish({
          width: image.naturalWidth || null,
          height: image.naturalHeight || null,
          durationMs: null,
        });
      image.onerror = () => finish(NOT_MEASURED);
      image.src = url;
      return;
    }

    const media = document.createElement(isVideo(mime) ? "video" : "audio");
    media.preload = "metadata";
    media.onloadedmetadata = () => {
      const seconds = media.duration;
      finish({
        width: media instanceof HTMLVideoElement ? media.videoWidth || null : null,
        height: media instanceof HTMLVideoElement ? media.videoHeight || null : null,
        durationMs: Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1_000) : null,
      });
    };
    media.onerror = () => finish(NOT_MEASURED);
    media.src = url;
  });
}

/** Ids dos membros citados com @ no corpo, para a mensagem otimista. */
function mentionIdsOf(state: AppState, body: string): string[] {
  const handles = new Set(extractHandles(body));
  if (handles.size === 0) return [];
  return state.users.filter(user => handles.has(user.handle.toLowerCase())).map(user => user.id);
}

/* -------------------------------------------------------------------- */
/* Linha "novas mensagens"                                               */
/* -------------------------------------------------------------------- */

/**
 * Congela onde comecam as novas: a marca e a hora da ultima mensagem ja lida,
 * entao a linha cai logo antes das `count` ultimas do historico. `null` quando
 * nao ha nada por ler; `0` quando tudo o que esta carregado e novo.
 */
function newSinceCut(list: Message[], count: number): number | null {
  if (count <= 0 || list.length === 0) return null;
  const index = list.length - count - 1;
  if (index < 0) return 0;
  const previous = list[index];
  return previous ? previous.createdAt : 0;
}

/** Aplica ou remove a marca de uma conversa sem tocar nas outras. */
function withNewSince(
  map: Record<string, number>,
  key: string,
  cut: number | null,
): Record<string, number> {
  if (cut === null) {
    if (!(key in map)) return map;
    const next = { ...map };
    delete next[key];
    return next;
  }
  if (map[key] === cut) return map;
  return { ...map, [key]: cut };
}

/* -------------------------------------------------------------------- */
/* Avisos do navegador                                                   */
/* -------------------------------------------------------------------- */

function notificationsSupported(): boolean {
  return typeof Notification !== "undefined";
}

function readNotificationPermission(): NotificationPrefs["permission"] {
  if (!notificationsSupported()) return "denied";
  const value = Notification.permission;
  return value === "granted" || value === "denied" ? value : "default";
}

function loadNotificationPrefs(): NotificationPrefs {
  const prefs: NotificationPrefs = {
    enabled: true,
    sound: true,
    permission: readNotificationPermission(),
  };
  try {
    const raw = window.localStorage.getItem(NOTIFY_PREFS_KEY);
    if (!raw) return prefs;
    const data = asRecord(JSON.parse(raw) as unknown);
    if (!data) return prefs;
    if (typeof data.enabled === "boolean") prefs.enabled = data.enabled;
    if (typeof data.sound === "boolean") prefs.sound = data.sound;
  } catch {
    /* sem armazenamento (modo privado): fica no padrao desta sessao */
  }
  return prefs;
}

function saveNotificationPrefs(prefs: NotificationPrefs): void {
  try {
    window.localStorage.setItem(
      NOTIFY_PREFS_KEY,
      JSON.stringify({ enabled: prefs.enabled, sound: prefs.sound }),
    );
  } catch {
    /* o navegador pode recusar a escrita; a preferencia vale so nesta sessao */
  }
}

function loadIgnoredUsers(): string[] {
  try {
    const raw = window.localStorage.getItem(IGNORED_USERS_KEY);
    if (!raw) return [];
    const data: unknown = JSON.parse(raw);
    return Array.isArray(data) ? data.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function saveIgnoredUsers(ids: string[]): void {
  try {
    window.localStorage.setItem(IGNORED_USERS_KEY, JSON.stringify(ids));
  } catch {
    /* sem armazenamento: a preferencia vale so nesta sessao */
  }
}

/** Trecho curto da mensagem para o corpo do aviso. */
function previewOf(message: Message): string {
  const body = message.body.trim();
  if (body.length > 0) {
    return body.length > NOTIFY_BODY_LIMIT ? `${body.slice(0, NOTIFY_BODY_LIMIT - 1)}…` : body;
  }
  const count = message.attachments.length;
  if (count === 1) return "Enviou um arquivo.";
  if (count > 1) return `Enviou ${count} arquivos.`;
  return "Nova mensagem.";
}

type AudioRef = { current: AudioContext | null };

/** O contexto de audio so nasce depois de um gesto do membro. */
function ensureAudio(ref: AudioRef): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  if (!ref.current) {
    try {
      ref.current = new AudioContext();
    } catch {
      return null;
    }
  }
  const ctx = ref.current;
  if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
  return ctx;
}

/**
 * Aviso sonoro curto, gerado na hora: duas notas de seno com queda rapida.
 * Nada de arquivo externo — o som nasce do proprio `AudioContext`.
 */
function playPing(ref: AudioRef): void {
  const ctx = ensureAudio(ref);
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(740, now);
    osc.frequency.setValueAtTime(988, now + 0.085);
    osc.connect(gain);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
    osc.start(now);
    osc.stop(now + 0.27);
  } catch {
    /* audio bloqueado pelo navegador: o aviso visual ja basta */
  }
}

/* -------------------------------------------------------------------- */
/* Provedor                                                              */
/* -------------------------------------------------------------------- */

export function AppProvider({
  sessionToken,
  onLogout,
  onSessionToken,
  children,
}: {
  sessionToken: string;
  onLogout: () => void;
  /**
   * Chamado quando o servidor emite um token novo no meio da sessao (troca de
   * senha, encerrar as outras sessoes). Quem esta por cima guarda o token e
   * repassa por `sessionToken`, o que reconecta o socket com ele.
   */
  onSessionToken: (token: string) => void;
  children: ReactNode;
}) {
  const [state, setState] = useState<AppState>(() => ({
    ...INITIAL,
    notifications: loadNotificationPrefs(),
    ignored: loadIgnoredUsers(),
  }));
  const stateRef = useRef(state);
  stateRef.current = state;

  /** Sobe a cada `ready` para refazer os pedidos que dependem da conexao. */
  const [readyTick, setReadyTick] = useState(0);

  const socketRef = useRef<LfrSocket | null>(null);
  const meshRef = useRef<VoiceMesh | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const kickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutRef = useRef(onLogout);
  logoutRef.current = onLogout;
  const applyTokenRef = useRef(onSessionToken);
  applyTokenRef.current = onSessionToken;

  /** Ultimo aviso de digitacao enviado por conversa. */
  const typingSentRef = useRef<Record<string, number>>({});
  /** Conversas cujo historico ja foi pedido nesta conexao. */
  /** Quem está ouvindo as respostas do painel (normalmente zero ou um). */
  const adminOuvintesRef = useRef(new Set<AdminOuvintes>());

  const historyAskedRef = useRef<Set<string>>(new Set());
  /** Termo enviado na busca em andamento; so a resposta dele vale. */
  const searchSentRef = useRef("");
  /** Envios em andamento, para poder cancelar. */
  const xhrRef = useRef<Map<string, XMLHttpRequest>>(new Map());
  /** Microfone escolhido pelo membro; injetado na captura da malha. */
  const micDeviceRef = useRef<string | null>(null);
  /** Enquanto troca o microfone, o aviso de "desconectado" e esperado. */
  const micSwitchRef = useRef(false);

  /** Conversas esperando o historico para congelar a linha de novas. */
  const pendingNewSinceRef = useRef<Map<string, number>>(new Map());
  /** Contexto de audio do aviso sonoro, criado sob demanda. */
  const audioRef = useRef<AudioContext | null>(null);
  /** Ultimo sinal de vida do membro nesta aba. */
  const lastActivityRef = useRef(Date.now());
  /** O `ausente` atual foi decidido pelo app, nao pelo membro. */
  const autoAwayRef = useRef(false);
  /** Aviso de mensagem nova; trocado a cada render para ver o estado atual. */
  const notifyRef = useRef<(message: Message) => void>(() => undefined);

  const patch = useCallback(
    (next: Partial<AppState> | ((prev: AppState) => Partial<AppState>)) => {
      setState(prev => ({ ...prev, ...(typeof next === "function" ? next(prev) : next) }));
    },
    [],
  );

  const updateUpload = useCallback(
    (uploadId: string, changes: Partial<PendingUpload>) => {
      patch(prev => ({
        uploads: prev.uploads.map(item =>
          item.id === uploadId ? { ...item, ...changes } : item,
        ),
      }));
    },
    [patch],
  );

  /* ------------------------------------------------------------------ */
  /* Envio de arquivos                                                   */
  /* ------------------------------------------------------------------ */

  /** `XMLHttpRequest` em vez de `fetch` porque so ele reporta progresso real. */
  const sendToServer = useCallback(
    (
      file: File,
      kind: "anexo" | "avatar",
      onProgress: (ratio: number) => void,
      register?: (xhr: XMLHttpRequest) => void,
    ): Promise<Attachment> =>
      new Promise<Attachment>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `/api/upload?kind=${kind}`);
        xhr.setRequestHeader("Authorization", `Bearer ${sessionToken}`);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.setRequestHeader("x-file-name", encodeURIComponent(file.name));

        xhr.upload.addEventListener("progress", event => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.min(1, event.loaded / event.total));
          }
        });
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const attachment = parseAttachment(xhr.responseText);
            if (!attachment) {
              reject(new Error("O servidor devolveu uma resposta inesperada para o arquivo."));
              return;
            }
            onProgress(1);
            resolve(attachment);
            return;
          }
          reject(new Error(uploadErrorMessage(xhr.status, xhr.responseText)));
        });
        xhr.addEventListener("error", () =>
          reject(new Error("A conexão caiu no meio do envio do arquivo.")),
        );
        xhr.addEventListener("abort", () => reject(new Error("Envio cancelado.")));

        register?.(xhr);
        xhr.send(file);
      }),
    [sessionToken],
  );

  /* ------------------------------------------------------------------ */
  /* Conexao                                                             */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const socket = connectSocket(sessionToken);
    socketRef.current = socket;

    socket.on("connect", () => patch({ reconnecting: false }));

    socket.on("disconnect", reason => {
      // Sair de proposito (expulsao, logout) nao e queda de conexao.
      patch({ reconnecting: reason !== "io client disconnect" });
    });

    socket.on("connect_error", error => {
      // Ja estava dentro: e queda passageira, mostra a faixa e segue tentando.
      if (stateRef.current.phase === "ready") {
        patch({ reconnecting: true });
        return;
      }
      patch({ phase: "error", error: error.message || "Não foi possível conectar." });
    });

    socket.on("ready", (snapshot: Snapshot) => {
      historyAskedRef.current.clear();
      pendingNewSinceRef.current.clear();
      setState(prev => {
        const fallback = makeChannelKey(snapshot.channels[0]?.id ?? DEFAULT_CHANNEL_ID);
        const known =
          prev.activeKey.startsWith("dm:") ||
          prev.activeKey.startsWith("voice:") ||
          snapshot.channels.some(channel => makeChannelKey(channel.id) === prev.activeKey);
        const active = known ? prev.activeKey : fallback;
        return {
          ...prev,
          phase: "ready",
          error: null,
          reconnecting: false,
          me: snapshot.me,
          platformFounder: snapshot.platformFounder,
          peerId: snapshot.peerId,
          users: snapshot.users,
          roles: snapshot.roles,
          channels: snapshot.channels,
          voiceChannels: snapshot.voiceChannels,
          settings: snapshot.settings,
          voiceStates: snapshot.voiceStates,
          unread: snapshot.unread,
          iceServers: snapshot.iceServers,
          maxUploadBytes: snapshot.maxUploadBytes,
          blocked: snapshot.blocked,
          roleSel: prev.roleSel || snapshot.me.roleId || snapshot.roles[0]?.id || "",
          activeKey: active,
          lastTextKey: active.startsWith("voice:") ? prev.lastTextKey : active,
        };
      });
      setReadyTick(tick => tick + 1);
    });

    socket.on("chat:history", ({ channelKey, messages, before, hasMore }) => {
      // Com cursor é uma página anterior: entra ANTES do que já está na tela,
      // sem mexer no resto. Sem cursor é a primeira carga, que substitui a
      // lista — é o mesmo evento fazendo as duas coisas, como o `before` do
      // Discord, e é o cursor ecoado que diz qual das duas.
      if (before) {
        patch(prev => {
          const atual = prev.messages[channelKey] ?? [];
          const conhecidos = new Set(atual.map(item => item.id));
          // Dedupe: entre pedir a página e ela chegar, uma dessas mensagens
          // pode ter subido por outro caminho (busca, "pular para").
          const anteriores = messages.filter(item => !conhecidos.has(item.id));
          return {
            messages: { ...prev.messages, [channelKey]: [...anteriores, ...atual] },
            historyHasMore: { ...prev.historyHasMore, [channelKey]: hasMore },
            historyLoading: { ...prev.historyLoading, [channelKey]: false },
          };
        });
        return;
      }

      // A marca de "novas mensagens" so pode ser calculada com o historico na mao.
      const pending = pendingNewSinceRef.current;
      const frozen = pending.get(channelKey);
      if (frozen !== undefined) pending.delete(channelKey);
      patch(prev => {
        const next: Partial<AppState> = {
          messages: { ...prev.messages, [channelKey]: messages },
          historyHasMore: { ...prev.historyHasMore, [channelKey]: hasMore },
          historyLoading: { ...prev.historyLoading, [channelKey]: false },
        };
        if (frozen !== undefined) {
          next.newSince = withNewSince(prev.newSince, channelKey, newSinceCut(messages, frozen));
        }
        return next;
      });
    });

    socket.on("chat:message", message => {
      setState(prev => {
        const list = prev.messages[message.channelKey] ?? [];
        const withoutOptimistic = message.clientId
          ? list.filter(item => item.clientId !== message.clientId)
          : list;
        if (withoutOptimistic.some(item => item.id === message.id)) return prev;
        // Quem acabou de mandar a mensagem nao esta mais digitando.
        const typingList = prev.typing[message.channelKey];
        const typing = typingList
          ? {
              ...prev.typing,
              [message.channelKey]: typingList.filter(item => item.userId !== message.authorId),
            }
          : prev.typing;
        return {
          ...prev,
          typing,
          messages: {
            ...prev.messages,
            [message.channelKey]: [...withoutOptimistic, message],
          },
        };
      });
      notifyRef.current(message);
    });

    socket.on("chat:updated", message => {
      setState(prev => {
        const list = prev.messages[message.channelKey];
        const inList = Boolean(list?.some(item => item.id === message.id));
        const inSearch = prev.search.results.some(item => item.id === message.id);
        if (!inList && !inSearch) return prev;
        return {
          ...prev,
          messages:
            inList && list
              ? {
                  ...prev.messages,
                  [message.channelKey]: list.map(item =>
                    item.id === message.id ? message : item,
                  ),
                }
              : prev.messages,
          search: inSearch
            ? {
                ...prev.search,
                results: prev.search.results.map(item =>
                  item.id === message.id ? message : item,
                ),
              }
            : prev.search,
          replyTo: prev.replyTo?.id === message.id ? message : prev.replyTo,
          editing: prev.editing?.id === message.id && message.deleted ? null : prev.editing,
        };
      });
    });

    socket.on("chat:unread", unread => patch({ unread }));

    socket.on("chat:typing", ({ channelKey, userId }) => {
      setState(prev => {
        if (prev.me && prev.me.id === userId) return prev;
        const list = prev.typing[channelKey] ?? [];
        const others = list.filter(entry => entry.userId !== userId);
        const entry: TypingEntry = { userId, at: Date.now() };
        return { ...prev, typing: { ...prev.typing, [channelKey]: [...others, entry] } };
      });
    });

    socket.on("chat:search", ({ query, messages }) => {
      // O servidor encurta o termo: basta ele ser o inicio do que foi pedido.
      if (query.length === 0 || !searchSentRef.current.startsWith(query)) return;
      patch(prev => ({ search: { ...prev.search, loading: false, results: messages } }));
    });

    socket.on("presence", ({ users }) => {
      patch(prev => ({
        users,
        me: prev.me ? (users.find(user => user.id === prev.me?.id) ?? prev.me) : prev.me,
      }));
    });

    socket.on("settings", settings => patch({ settings }));
    socket.on("roles", ({ roles }) => patch({ roles }));

    socket.on("admin:auditLog", ({ entries, before }) => {
      patch(prev => ({
        // Primeira página (sem `before`) troca a lista; páginas seguintes emendam.
        auditLog: before === undefined ? entries : [...(prev.auditLog ?? []), ...entries],
        auditLogLoading: false,
      }));
    });

    socket.on("channels", ({ channels, voiceChannels }) => {
      setState(prev => {
        const exists = (key: string) =>
          !key.startsWith("ch:") || channels.some(item => makeChannelKey(item.id) === key);
        const fallback = makeChannelKey(channels[0]?.id ?? DEFAULT_CHANNEL_ID);
        const activeKey = exists(prev.activeKey) ? prev.activeKey : fallback;
        const lastTextKey = exists(prev.lastTextKey) ? prev.lastTextKey : fallback;
        return { ...prev, channels, voiceChannels, activeKey, lastTextKey };
      });
    });

    socket.on("kicked", ({ reason }) => {
      meshRef.current?.destroy();
      meshRef.current = null;
      patch({
        kicked: reason,
        reconnecting: false,
        voiceId: null,
        remote: {},
        local: { mic: null, cam: null, screen: null },
      });
      socket.disconnect();
      if (kickTimer.current) clearTimeout(kickTimer.current);
      kickTimer.current = setTimeout(() => logoutRef.current(), KICK_EXIT_MS);
    });

    socket.on("lfr:error", ({ message }) => patch({ notice: message }));

    socket.on("voice:states", ({ voiceStates }) => {
      patch(prev => {
        const present = new Set(voiceStates.map(item => item.peerId));
        const remote = Object.fromEntries(
          Object.entries(prev.remote).filter(([peerId]) => present.has(peerId)),
        );
        return { voiceStates, remote };
      });
    });

    // A moderação tirou o próprio membro da chamada, ou mudou ele de sala.
    // "Mover" não recria a malha: só troca `voiceId`/`activeKey` — o efeito
    // que já sincroniza peers a partir do estado de voz reconecta sozinho
    // pra quem estiver na sala nova.
    socket.on("voice:forced", ({ action, voiceId }) => {
      if (action === "disconnect") {
        meshRef.current?.destroy();
        meshRef.current = null;
        patch(prev => ({
          voiceId: null,
          sharing: false,
          cam: false,
          focus: null,
          remote: {},
          local: { mic: null, cam: null, screen: null },
          stats: {},
          speaking: {},
          activeKey: prev.activeKey.startsWith("voice:") ? prev.lastTextKey : prev.activeKey,
        }));
        return;
      }
      if (action === "move" && voiceId) {
        patch({ voiceId, activeKey: `voice:${voiceId}`, focus: null });
      }
    });

    socket.on("users:blocked", ({ blocked }) => patch({ blocked }));

    socket.on("servers", ({ servers, activeServerId, available }) =>
      patch({ servers, activeServerId, serversAvailable: available }),
    );

    socket.on("servers:invite", ({ serverId, code }) => patch({ inviteCode: { serverId, code } }));

    /*
      Painel dos Fundadores.

      O store não conhece cada evento do painel: ele repassa o que chegar para
      quem assinou. É o que permite acrescentar recurso administrativo sem
      mexer no núcleo do app — que era o pedido de manter isso modular.
    */
    for (const nome of [
      "admin:overview",
      "admin:users",
      "admin:user",
      "admin:servers",
      "admin:server",
      "admin:audit",
      "admin:flags",
      "admin:ok",
      "admin:banidos",
      "admin:sessoesFundadores",
      "admin:evento",
      "admin:crescimento",
    ] as const) {
      socket.on(nome as never, ((carga: unknown) => {
        for (const ouvinte of adminOuvintesRef.current) {
          const chave = ADMIN_CHAVE[nome];
          const fn = ouvinte[chave] as ((c: unknown) => void) | undefined;
          fn?.(carga);
        }
      }) as never);
    }

    /*
      Banimento: a tela inteira trava atrás do aviso.

      Não desconectamos aqui de propósito. A pessoa precisa poder LER por que
      foi banida — derrubar a conexão junto com o aviso apagaria a explicação
      antes de ela ser lida. A sessão no banco já foi invalidada pelo servidor,
      então nada do que ela tente daqui em diante funciona.
    */
    socket.on("platform:banned", aviso => patch({ banimento: aviso }));

    socket.on("platform:serverRemoved", ({ motivo }) => {
      patch({
        notice: `Este servidor foi removido pela equipe do LFRCORD. Motivo: ${motivo}`,
      });
    });

    socket.on("platform:announcement", aviso => patch({ avisoDaPlataforma: aviso }));

    socket.on("members:role", ({ serverId, userId, roleIds }) => {
      patch(prev => {
        // Cargo é por servidor: aplicar um cargo de outro lugar deixaria a
        // tela mostrando um id que não existe na lista de cargos aberta.
        if (prev.activeServerId !== null && prev.activeServerId !== serverId) return {};
        // O primeiro da lista é o mais alto — o servidor já manda ordenado, e
        // é ele que colore o nome e agrupa no painel.
        const maisAlto = roleIds[0];
        const aplicar = (user: User): User =>
          user.id === userId ? { ...user, roleIds, ...(maisAlto ? { roleId: maisAlto } : {}) } : user;
        return {
          users: prev.users.map(aplicar),
          /*
            `me` TAMBÉM, quando o cargo é meu.

            `me` é uma cópia à parte de `users`, e só o `presence` a
            reconciliava — o que fazia o cargo recebido demorar até a próxima
            volta de presença para valer na MINHA tela. Quem deu via na hora
            (o alvo estava em `users`); quem recebeu ficava com os botões
            antigos, porque tudo que pergunta "o que EU posso" lê daqui.
          */
          me: prev.me ? aplicar(prev.me) : prev.me,
        };
      });
    });

    socket.on("servers:content", ({ serverId, channels, voiceChannels, roles, settings, members }) => {
      patch(prev => {
        // Trocar de servidor troca o conteúdo inteiro da lateral. A conversa
        // aberta precisa ir junto: continuar num canal do servidor anterior
        // deixaria a tela mostrando algo que a barra já não lista.
        const primeiro = channels[0];
        const aindaVale = channels.some(canal => makeChannelKey(canal.id) === prev.activeKey);
        const activeKey =
          prev.activeKey.startsWith("dm:") || aindaVale
            ? prev.activeKey
            : primeiro
              ? makeChannelKey(primeiro.id)
              : prev.activeKey;

        // `users` guarda quem eu enxergo; os membros deste servidor entram
        // sem apagar amigos de fora dele.
        const porId = new Map(prev.users.map(user => [user.id, user]));
        for (const member of members) porId.set(member.id, member);

        return {
          activeServerId: serverId,
          channels,
          voiceChannels,
          roles,
          settings,
          users: [...porId.values()],
          serverMemberIds: members.map(member => member.id),
          activeKey,
          lastTextKey: activeKey.startsWith("dm:") ? prev.lastTextKey : activeKey,
        };
      });
    });

    socket.on("friends", ({ links, profiles, conversations, available }) => {
      // Substituicao inteira, nao merge: o servidor manda o estado completo
      // das relacoes a cada mudanca, e um merge deixaria para tras o vinculo
      // que acabou de ser desfeito.
      const byId: Record<string, FriendProfile> = {};
      for (const profile of profiles) byId[profile.id] = profile;
      patch({
        friends: links,
        friendProfiles: byId,
        conversations,
        friendsAvailable: available,
      });
    });

    socket.on("rtc:signal", ({ from, signal }) => {
      meshRef.current?.handleSignal(from, signal);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [patch, sessionToken]);

  /**
   * Pede o historico da conversa aberta uma vez por conexao e guarda quantas
   * mensagens estavam por ler, para congelar a linha de "novas mensagens".
   */
  useEffect(() => {
    if (state.phase !== "ready") return;
    const key = state.activeKey;
    if (key.startsWith("voice:")) return;
    if (historyAskedRef.current.has(key)) return;
    historyAskedRef.current.add(key);
    const count = state.unread[key]?.count ?? 0;
    if (count > 0) pendingNewSinceRef.current.set(key, count);
    socketRef.current?.emit("chat:history", { channelKey: key });
  }, [readyTick, state.activeKey, state.phase, state.unread]);

  /**
   * A conversa aberta nunca fica com nao-lidas: alem do clique, o que chega
   * enquanto ela esta na tela ja entra como lido, e a marca vale no servidor.
   */
  useEffect(() => {
    if (state.phase !== "ready") return;
    const key = state.activeKey;
    if (key.startsWith("voice:")) return;
    const entry = state.unread[key];
    if (!entry || (entry.count === 0 && entry.mentions === 0)) return;
    socketRef.current?.emit("chat:read", { channelKey: key });
    patch(prev => ({ unread: { ...prev.unread, [key]: { count: 0, mentions: 0 } } }));
  }, [patch, state.activeKey, state.phase, state.unread]);

  /* ------------------------------------------------------------------ */
  /* Titulo da aba                                                       */
  /* ------------------------------------------------------------------ */

  /** Devolve o titulo original quando o app sai da tela. */
  useEffect(() => {
    const original = document.title;
    return () => {
      document.title = original;
    };
  }, []);

  /** Contador de nao-lidas no titulo; o arroba marca que ha mencao. */
  useEffect(() => {
    const base = state.settings.name.trim() || "LFRCORD";
    let total = 0;
    let mentions = 0;
    for (const entry of Object.values(state.unread)) {
      total += entry.count;
      mentions += entry.mentions;
    }
    document.title = total > 0 ? `(${mentions > 0 ? "@" : ""}${total}) ${base}` : base;
  }, [state.settings.name, state.unread]);

  /* ------------------------------------------------------------------ */
  /* Ausente automatico                                                  */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (state.phase !== "ready") return;

    const applyAuto = (status: "online" | "ausente", auto: boolean) => {
      autoAwayRef.current = auto;
      setState(prev => {
        if (!prev.me || prev.me.status === status) return { ...prev, autoAway: auto };
        const me: User = { ...prev.me, status };
        return {
          ...prev,
          autoAway: auto,
          me,
          users: prev.users.map(user => (user.id === me.id ? { ...user, status } : user)),
        };
      });
      socketRef.current?.emit("presence:status", { status });
    };

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      if (!autoAwayRef.current) return;
      applyAuto("online", false);
    };

    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, onActivity, { passive: true });
    }

    const timer = window.setInterval(() => {
      const me = stateRef.current.me;
      // `ocupado` e `invisivel` sao escolha do membro: o app nao mexe neles.
      if (!me || me.status !== "online") return;
      if (Date.now() - lastActivityRef.current < AUTO_AWAY_MS) return;
      applyAuto("ausente", true);
    }, AWAY_CHECK_MS);

    return () => {
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, onActivity);
      window.clearInterval(timer);
    };
  }, [state.phase]);

  /* ------------------------------------------------------------------ */
  /* Atalhos de teclado                                                  */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "k") {
        if (stateRef.current.phase !== "ready") return;
        event.preventDefault();
        setState(prev =>
          prev.switcherOpen ? prev : { ...prev, switcherOpen: true, reactionPickerFor: null },
        );
        return;
      }
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const current = stateRef.current;
      // O seletor rapido fecha mesmo com o foco no campo de busca dele.
      if (current.switcherOpen) {
        patch({ switcherOpen: false });
        return;
      }
      // Quem esta digitando resolve o proprio Esc (cancelar resposta, edicao...).
      if (isEditableTarget(event.target)) return;
      if (current.reactionPickerFor) {
        patch({ reactionPickerFor: null });
        return;
      }
      if (current.focus) {
        patch({ focus: null });
        return;
      }
      if (current.profileEditorOpen) {
        patch({ profileEditorOpen: false });
        return;
      }
      if (current.profile) {
        patch({ profile: null });
        return;
      }
      if (current.settingsOpen) {
        patch({ settingsOpen: false, saved: false });
        return;
      }
      if (current.search.open) {
        searchSentRef.current = "";
        patch({ search: { open: false, query: "", loading: false, results: [] } });
        return;
      }
      if (current.editing) {
        patch({ editing: null });
        return;
      }
      if (current.replyTo) patch({ replyTo: null });
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [patch]);

  /** A permissao pode mudar nas configuracoes do navegador, fora do app. */
  useEffect(() => {
    const sync = () => {
      const permission = readNotificationPermission();
      setState(prev =>
        prev.notifications.permission === permission
          ? prev
          : { ...prev, notifications: { ...prev.notifications, permission } },
      );
    };
    window.addEventListener("focus", sync);
    return () => window.removeEventListener("focus", sync);
  }, []);

  /* ------------------------------------------------------------------ */
  /* Dispositivos de audio                                               */
  /* ------------------------------------------------------------------ */

  const refreshDevices = useCallback(async () => {
    const media = navigator.mediaDevices;
    if (!media || typeof media.enumerateDevices !== "function") return;
    try {
      const list = await media.enumerateDevices();
      patch(prev => ({
        devices: {
          ...prev.devices,
          inputs: list.filter(device => device.kind === "audioinput"),
          outputs: list.filter(device => device.kind === "audiooutput"),
        },
      }));
    } catch {
      /* sem permissao o navegador nao lista nada; a interface segue vazia */
    }
  }, [patch]);

  useEffect(() => {
    void refreshDevices();
    const media = navigator.mediaDevices;
    if (!media || typeof media.addEventListener !== "function") return;
    const onChange = () => void refreshDevices();
    media.addEventListener("devicechange", onChange);
    return () => media.removeEventListener("devicechange", onChange);
  }, [refreshDevices]);

  /**
   * A malha captura o microfone sem saber do dispositivo escolhido aqui; este
   * desvio injeta o `deviceId` nos pedidos de audio feitos pelo proprio app e
   * volta ao padrao se o aparelho escolhido sumir.
   */
  useEffect(() => {
    const media = navigator.mediaDevices;
    if (!media || typeof media.getUserMedia !== "function") return;
    const original = media.getUserMedia.bind(media);
    media.getUserMedia = (constraints?: MediaStreamConstraints): Promise<MediaStream> => {
      const wanted = micDeviceRef.current;
      if (!wanted || !constraints || !constraints.audio || constraints.video) {
        return original(constraints);
      }
      const audio: MediaTrackConstraints =
        typeof constraints.audio === "object" ? { ...constraints.audio } : {};
      audio.deviceId = { exact: wanted };
      return original({ ...constraints, audio }).catch((error: unknown) => {
        const name = error instanceof Error ? error.name : "";
        if (name !== "OverconstrainedError" && name !== "ConstraintNotSatisfiedError") throw error;
        micDeviceRef.current = null;
        patch(prev => ({ devices: { ...prev.devices, inputId: null } }));
        return original(constraints);
      });
    };
    return () => {
      media.getUserMedia = original;
    };
  }, [patch]);

  /* ------------------------------------------------------------------ */
  /* Malha WebRTC                                                        */
  /* ------------------------------------------------------------------ */
  const ensureMesh = useCallback((): VoiceMesh | null => {
    const socket = socketRef.current;
    const current = stateRef.current;
    if (!socket || !current.peerId) return null;
    if (meshRef.current) return meshRef.current;
    const mesh = new VoiceMesh({
      socket,
      selfPeerId: current.peerId,
      iceServers: current.iceServers,
      shareQuality: current.shareQuality,
      camQuality: current.camQuality,
      contentHint: current.contentHint,
      noiseSuppression: current.noiseSuppression,
      callbacks: {
        onRemoteStream: (peerId, kind, stream) =>
          patch(prev => ({
            remote: {
              ...prev.remote,
              [peerId]: { ...(prev.remote[peerId] ?? emptyRemote()), [kind]: stream },
            },
          })),
        onSpeaking: (peerId, speaking) => {
          patch(prev =>
            prev.speaking[peerId] === speaking
              ? {}
              : { speaking: { ...prev.speaking, [peerId]: speaking } },
          );
          if (peerId === stateRef.current.peerId) {
            socketRef.current?.emit("voice:state", { speaking });
          }
        },
        onStats: stats => patch({ stats }),
        onLocalStreams: local => patch({ local }),
        onError: message => {
          // A troca de microfone derruba a trilha de proposito: nao e falha.
          if (micSwitchRef.current) return;
          patch({ notice: message });
        },
        onShareEnded: () => {
          patch({ sharing: false, lastShareResult: null });
          socketRef.current?.emit("voice:state", { sharing: false });
        },
        onCamEnded: () => {
          patch({ cam: false });
          socketRef.current?.emit("voice:state", { cam: false });
        },
        onShareStarted: result => {
          patch({ lastShareResult: { ...result, at: Date.now() } });
          if (!result.matchesRequest) {
            const preset = QUALITY_PRESETS[stateRef.current.shareQuality];
            patch({
              notice: `A fonte capturada não aceitou ${preset.label} — saiu em ${result.width}×${result.height} a ${result.frameRate} fps.`,
            });
          } else if (stateRef.current.shareSystemAudio && result.systemAudioSource === "none") {
            patch({
              notice:
                "Não foi possível capturar o áudio do sistema com segurança neste PC — a transmissão saiu sem áudio do computador (o microfone continua normal).",
            });
          }
        },
      },
    });
    meshRef.current = mesh;
    return mesh;
  }, [patch]);

  /** Mantem as conexoes alinhadas com os participantes da sala atual. */
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    if (!state.voiceId) {
      mesh.syncPeers([]);
      return;
    }
    const peers = state.voiceStates
      .filter(item => item.voiceId === state.voiceId && item.peerId !== state.peerId)
      .map(item => item.peerId);
    mesh.syncPeers(peers);
  }, [state.peerId, state.voiceId, state.voiceStates]);

  /** Silenciado ou em timeout: o microfone fica fechado de qualquer jeito. */
  useEffect(() => {
    const blocked = Boolean(state.me?.serverMuted) || isTimedOut(state.me);
    if (!blocked || state.muted) return;
    meshRef.current?.setMuted(true);
    patch({ muted: true });
  }, [patch, state.me?.serverMuted, state.me?.timeoutUntil, state.muted]);

  /** Ensurdecido por um moderador: nem ouve nem fala, até tirarem — igual ao silenciado, só que também trava o áudio recebido. */
  useEffect(() => {
    if (!state.me?.serverDeafened || (state.deafened && state.muted)) return;
    meshRef.current?.setMuted(true);
    patch({ deafened: true, muted: true });
  }, [patch, state.me?.serverDeafened, state.deafened, state.muted]);

  /** Limpa sozinho o aviso de digitacao de quem parou de escrever. */
  const typingCount = useMemo(
    () => Object.values(state.typing).reduce((total, list) => total + list.length, 0),
    [state.typing],
  );

  useEffect(() => {
    if (typingCount === 0) return;
    const timer = window.setInterval(() => {
      const cutoff = Date.now() - TYPING_TTL_MS;
      setState(prev => {
        let changed = false;
        const next: Record<string, TypingEntry[]> = {};
        for (const [key, list] of Object.entries(prev.typing)) {
          const kept = list.filter(entry => entry.at > cutoff);
          if (kept.length !== list.length) changed = true;
          if (kept.length > 0) next[key] = kept;
        }
        return changed ? { ...prev, typing: next } : prev;
      });
    }, TYPING_SWEEP_MS);
    return () => window.clearInterval(timer);
  }, [typingCount]);

  /** Push-to-talk: o microfone so abre enquanto a barra de espaco esta pressionada. */
  useEffect(() => {
    if (!state.pushToTalk || !state.voiceId) return;

    const openMic = (open: boolean) => {
      const muted = !open;
      if (stateRef.current.muted === muted) return;
      meshRef.current?.setMuted(muted);
      patch({ muted });
      socketRef.current?.emit("voice:state", { muted });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" || event.repeat) return;
      if (isEditableTarget(event.target)) return;
      if (stateRef.current.deafened || stateRef.current.me?.serverMuted) return;
      if (isTimedOut(stateRef.current.me)) return;
      event.preventDefault();
      openMic(true);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code !== "Space") return;
      openMic(false);
    };
    const onBlur = () => openMic(false);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [patch, state.pushToTalk, state.voiceId]);

  useEffect(() => {
    const pending = xhrRef.current;
    const audio = audioRef;
    return () => {
      meshRef.current?.destroy();
      meshRef.current = null;
      if (savedTimer.current) clearTimeout(savedTimer.current);
      if (kickTimer.current) clearTimeout(kickTimer.current);
      for (const xhr of pending.values()) xhr.abort();
      pending.clear();
      const ctx = audio.current;
      audio.current = null;
      if (ctx) void ctx.close().catch(() => undefined);
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /* Acoes                                                               */
  /* ------------------------------------------------------------------ */
  const openKey = useCallback(
    (key: string, tab?: SideTab) => {
      searchSentRef.current = "";
      const current = stateRef.current;
      // A linha de novas congela agora, com o que ainda estava por ler.
      const waiting = current.unread[key]?.count ?? 0;
      const loaded = current.messages[key];
      const pending = pendingNewSinceRef.current;
      pending.delete(key);
      if (!loaded && waiting > 0) pending.set(key, waiting);
      const cut = loaded ? newSinceCut(loaded, waiting) : null;

      patch(prev => ({
        activeKey: key,
        lastTextKey: key,
        tab: tab ?? prev.tab,
        unread: { ...prev.unread, [key]: { count: 0, mentions: 0 } },
        newSince: loaded || waiting === 0 ? withNewSince(prev.newSince, key, cut) : prev.newSince,
        focus: null,
        replyTo: null,
        editing: null,
        reactionPickerFor: null,
        switcherOpen: false,
        search: { open: false, query: "", loading: false, results: [] },
      }));
      socketRef.current?.emit("chat:read", { channelKey: key });
    },
    [patch],
  );

  /** Refaz a captura do microfone e troca a trilha ja publicada na malha. */
  const restartMic = useCallback(async () => {
    const mesh = meshRef.current;
    const current = stateRef.current;
    if (!mesh || !current.voiceId) return;
    const track = current.local.mic?.getAudioTracks()[0] ?? null;
    if (track) {
      micSwitchRef.current = true;
      track.stop();
      // `stop()` nao dispara `ended`: o aviso manual faz a malha soltar a trilha.
      track.dispatchEvent(new Event("ended"));
      micSwitchRef.current = false;
    }
    await mesh.enableMic();
    mesh.setMuted(stateRef.current.muted);
    void refreshDevices();
  }, [refreshDevices]);

  /** Guarda a preferencia no navegador e no estado, de uma vez so. */
  const savePrefs = useCallback(
    (changes: Partial<NotificationPrefs>) => {
      const prefs: NotificationPrefs = {
        ...stateRef.current.notifications,
        permission: readNotificationPermission(),
        ...changes,
      };
      saveNotificationPrefs(prefs);
      patch({ notifications: prefs });
      return prefs;
    },
    [patch],
  );

  /**
   * Pede a permissao do navegador. So pode sair de um clique do membro — nunca
   * no carregamento — e aproveita o gesto para liberar o audio do aviso.
   */
  const askPermission = useCallback(() => {
    if (!notificationsSupported()) {
      patch({ notice: "Este navegador não oferece avisos na área de trabalho." });
      return;
    }
    ensureAudio(audioRef);
    const permission = readNotificationPermission();
    if (permission === "granted") {
      savePrefs({ permission });
      return;
    }
    if (permission === "denied") {
      savePrefs({ permission });
      patch({
        notice: "Os avisos estão bloqueados para este site nas permissões do navegador.",
      });
      return;
    }
    void Notification.requestPermission().then(
      result => {
        const value: NotificationPrefs["permission"] =
          result === "granted" || result === "denied" ? result : "default";
        savePrefs({ permission: value });
        if (value === "granted") {
          patch({ notice: "Avisos ligados neste navegador." });
          return;
        }
        if (value === "denied") {
          patch({ notice: "Os avisos ficaram bloqueados para este site." });
        }
      },
      () => patch({ notice: "Não foi possível pedir a permissão de avisos." }),
    );
  }, [patch, savePrefs]);

  /**
   * Aviso de mencao e de conversa direta, so com a aba fora de foco. O som sai
   * mesmo sem permissao do navegador; a janelinha depende dela.
   */
  const notifyIncoming = useCallback(
    (message: Message) => {
      const current = stateRef.current;
      const me = current.me;
      if (!me || message.deleted || message.authorId === me.id) return;

      const prefs = current.notifications;
      if (!prefs.enabled) return;

      const isDm = message.channelKey.startsWith("dm:");
      const isMention = message.mentions.includes(me.id);
      if (!isDm && !isMention) return;
      if (document.hasFocus()) return;

      if (prefs.sound) playPing(audioRef);
      if (!notificationsSupported() || prefs.permission !== "granted") return;

      const author = current.users.find(user => user.id === message.authorId);
      const authorName = author?.name ?? "Alguém";
      const channel = current.channels.find(
        item => makeChannelKey(item.id) === message.channelKey,
      );
      const title = isDm
        ? `Mensagem de ${authorName}`
        : `${authorName} mencionou você em #${channel?.name ?? "canal"}`;

      try {
        const aviso = new Notification(title, {
          body: previewOf(message),
          tag: message.channelKey,
          icon: author?.avatarUrl ?? undefined,
          // O som e o do proprio app quando ligado; sem ele, vale o do sistema.
          silent: prefs.sound,
        });
        aviso.onclick = () => {
          window.focus();
          openKey(message.channelKey, isDm ? "diretas" : "canais");
          aviso.close();
        };
      } catch {
        /* o navegador pode recusar o aviso; o app segue igual */
      }
    },
    [openKey],
  );
  notifyRef.current = notifyIncoming;

  const actions = useMemo<AppActions>(() => {
    // `emit` depende do `this` do socket: sempre chamado como metodo
    // (`socket.emit(...)`), nunca extraido para uma variavel solta.
    const sock = (): LfrSocket | null => socketRef.current;

    return {
      selectChannel: channelId => openKey(makeChannelKey(channelId), "canais"),

      selectDm: userId => {
        const me = stateRef.current.me;
        if (!me) return;
        openKey(dmKey(me.id, userId), "diretas");
      },

      selectVoiceView: voiceId => patch({ activeKey: `voice:${voiceId}`, focus: null }),
      setTab: tab => patch({ tab }),
      backToText: () => patch(prev => ({ activeKey: prev.lastTextKey, focus: null })),

      setDraft: value => patch(prev => ({ drafts: { ...prev.drafts, [prev.activeKey]: value } })),

      send: () => {
        const current = stateRef.current;
        const key = current.activeKey;
        if (key.startsWith("voice:") || !current.me) return;

        if (current.uploads.some(item => !item.attachment && !item.error)) {
          patch({ notice: "Espere o envio dos arquivos terminar." });
          return;
        }
        const attachments = current.uploads
          .map(item => item.attachment)
          .filter((item): item is Attachment => item !== null);

        const body = (current.drafts[key] ?? "").trim();
        if (!body && attachments.length === 0) return;
        if (body.length > MAX_MESSAGE_LENGTH) {
          patch({ notice: `A mensagem passa de ${MAX_MESSAGE_LENGTH} caracteres.` });
          return;
        }

        const clientId = newId("c");
        const replyToId = current.replyTo?.id ?? null;
        const optimistic: Message = {
          id: clientId,
          channelKey: key,
          authorId: current.me.id,
          body,
          createdAt: Date.now(),
          editedAt: null,
          replyToId,
          attachments,
          reactions: [],
          mentions: mentionIdsOf(current, body),
          deleted: false,
          clientId,
        };

        patch(prev => ({
          drafts: { ...prev.drafts, [key]: "" },
          messages: { ...prev.messages, [key]: [...(prev.messages[key] ?? []), optimistic] },
          uploads: [],
          replyTo: null,
          reactionPickerFor: null,
        }));
        typingSentRef.current[key] = 0;
        sock()?.emit("chat:send", { channelKey: key, body, clientId, replyToId, attachments });
      },

      attachFiles: files => {
        const limit = stateRef.current.maxUploadBytes || MAX_UPLOAD_BYTES;
        for (const file of files) {
          const id = newId("u");
          const entry: PendingUpload = {
            id,
            name: file.name,
            size: file.size,
            mime: file.type || "application/octet-stream",
            progress: 0,
            error: null,
            attachment: null,
          };
          if (file.size > limit) {
            const tooBig: PendingUpload = {
              ...entry,
              error: `Arquivo maior que o limite de ${formatBytes(limit)}.`,
            };
            patch(prev => ({ uploads: [...prev.uploads, tooBig] }));
            continue;
          }
          patch(prev => ({ uploads: [...prev.uploads, entry] }));

          void (async () => {
            try {
              const measured = await measureFile(file);
              const attachment = await sendToServer(
                file,
                "anexo",
                ratio => updateUpload(id, { progress: ratio }),
                xhr => xhrRef.current.set(id, xhr),
              );
              updateUpload(id, {
                progress: 1,
                error: null,
                attachment: {
                  ...attachment,
                  width: attachment.width ?? measured.width,
                  height: attachment.height ?? measured.height,
                  durationMs: attachment.durationMs ?? measured.durationMs,
                },
              });
            } catch (error) {
              updateUpload(id, {
                error: messageOf(error, "Não foi possível enviar o arquivo."),
              });
            } finally {
              xhrRef.current.delete(id);
            }
          })();
        }
      },

      removeUpload: uploadId => {
        const xhr = xhrRef.current.get(uploadId);
        if (xhr) {
          xhr.abort();
          xhrRef.current.delete(uploadId);
        }
        patch(prev => ({ uploads: prev.uploads.filter(item => item.id !== uploadId) }));
      },

      setReplyTo: message => patch({ replyTo: message, editing: null }),
      startEditing: message => patch({ editing: message, replyTo: null }),

      submitEdit: body => {
        const editing = stateRef.current.editing;
        if (!editing) return;
        const trimmed = body.trim();
        if (!trimmed) {
          patch({ notice: "A mensagem não pode ficar vazia. Apague-a se quiser removê-la." });
          return;
        }
        if (trimmed.length > MAX_MESSAGE_LENGTH) {
          patch({ notice: `A mensagem passa de ${MAX_MESSAGE_LENGTH} caracteres.` });
          return;
        }
        patch({ editing: null });
        if (trimmed === editing.body) return;
        sock()?.emit("chat:edit", { messageId: editing.id, body: trimmed });
      },

      deleteMessage: messageId => {
        patch(prev => ({
          editing: prev.editing?.id === messageId ? null : prev.editing,
          replyTo: prev.replyTo?.id === messageId ? null : prev.replyTo,
          reactionPickerFor: prev.reactionPickerFor === messageId ? null : prev.reactionPickerFor,
        }));
        sock()?.emit("chat:delete", { messageId });
      },

      toggleReaction: (messageId, emoji) => {
        patch({ reactionPickerFor: null });
        sock()?.emit("chat:react", { messageId, emoji });
      },

      openReactionPicker: messageId => patch({ reactionPickerFor: messageId }),

      notifyTyping: () => {
        const current = stateRef.current;
        const key = current.activeKey;
        if (key.startsWith("voice:") || !current.me) return;
        const now = Date.now();
        if (now - (typingSentRef.current[key] ?? 0) < TYPING_THROTTLE_MS) return;
        typingSentRef.current[key] = now;
        sock()?.emit("chat:typing", { channelKey: key });
      },

      openSearch: () => patch(prev => ({ search: { ...prev.search, open: true } })),

      closeSearch: () => {
        searchSentRef.current = "";
        patch({ search: { open: false, query: "", loading: false, results: [] } });
      },

      runSearch: query => {
        const trimmed = query.trim();
        if (trimmed.length < 2) {
          searchSentRef.current = "";
          patch({ search: { open: true, query, loading: false, results: [] } });
          return;
        }
        searchSentRef.current = trimmed;
        patch(prev => ({ search: { ...prev.search, open: true, query, loading: true } }));
        sock()?.emit("chat:search", { query: trimmed, channelKey: null });
      },

      jumpToMessage: message => {
        openKey(message.channelKey, message.channelKey.startsWith("dm:") ? "diretas" : "canais");
      },

      adminEmit: (evento, carga) => {
        // `as never` porque o mapa de eventos é tipado por nome, e aqui o nome
        // é dinâmico de propósito: é o que mantém a ponte fina.
        // O socket e tipado por nome de evento; aqui o nome e dinamico de
        // proposito. O cast some com a checagem, que ja aconteceu no contrato
        // do painel — e o servidor reconfere tudo de novo, inclusive quem e.
        (sock() as unknown as { emit: (e: string, c: unknown) => void } | undefined)?.emit(
          evento,
          carga,
        );
      },

      adminSubscribe: ouvintes => {
        adminOuvintesRef.current.add(ouvintes);
        return () => {
          adminOuvintesRef.current.delete(ouvintes);
        };
      },

      abrirPainelFundadores: () => patch({ painelAberto: true, settingsOpen: false }),
      fecharPainelFundadores: () => patch({ painelAberto: false }),

      aceitarBanimento: () => {
        // Sair de verdade: apagar a sessão local e recarregar. Só esconder o
        // aviso devolveria o app para alguém que não pode mais usá-lo.
        try {
          window.localStorage.removeItem(SESSION_STORAGE_KEY);
        } catch {
          /* navegador sem armazenamento: o reload resolve mesmo assim */
        }
        window.location.reload();
      },

      loadOlderMessages: channelKey => {
        const current = stateRef.current;
        // Três portas, e todas importam: sala de voz não tem histórico; canal
        // que já chegou ao começo não tem o que pedir; e pedido em voo não
        // vira dois — a rolagem dispara em rajada, e sem esta trava o mesmo
        // trecho voltaria repetido.
        if (channelKey.startsWith("voice:")) return;
        if (current.historyHasMore[channelKey] === false) return;
        if (current.historyLoading[channelKey]) return;
        const lista = current.messages[channelKey] ?? [];
        // A otimista ainda não existe no banco — o id dela é o `clientId`, e
        // um cursor assim não acha linha nenhuma. O cursor tem que ser a mais
        // antiga que o servidor já confirmou.
        const maisAntiga = lista.find(item => item.id !== item.clientId);
        if (!maisAntiga) return;
        patch(prev => ({
          historyLoading: { ...prev.historyLoading, [channelKey]: true },
        }));
        sock()?.emit("chat:history", {
          channelKey,
          before: { createdAt: maisAntiga.createdAt, id: maisAntiga.id },
          limit: HISTORY_PAGE,
        });
      },

      joinVoice: voiceId => {
        const current = stateRef.current;
        if (!can(current, "voz")) {
          patch({ notice: "Você não tem permissão para entrar nas salas de voz." });
          return;
        }
        const mesh = ensureMesh();
        patch({ voiceId, activeKey: `voice:${voiceId}`, focus: null, switcherOpen: false });
        sock()?.emit("voice:join", { voiceId });
        void mesh?.enableMic().then(() => {
          meshRef.current?.setMuted(stateRef.current.muted);
          void refreshDevices();
        });
      },

      leaveVoice: () => {
        sock()?.emit("voice:leave");
        meshRef.current?.destroy();
        meshRef.current = null;
        patch(prev => ({
          voiceId: null,
          sharing: false,
          cam: false,
          focus: null,
          remote: {},
          local: { mic: null, cam: null, screen: null },
          stats: {},
          speaking: {},
          activeKey: prev.activeKey.startsWith("voice:") ? prev.lastTextKey : prev.activeKey,
        }));
      },

      toggleMute: () => {
        const current = stateRef.current;
        if (current.muted && current.me?.serverMuted) {
          patch({ notice: "Você foi silenciado por um moderador da comunidade." });
          return;
        }
        if (current.muted && isTimedOut(current.me)) {
          patch({ notice: "Você está em timeout e não pode abrir o microfone." });
          return;
        }
        if (current.muted && current.me?.serverDeafened) {
          patch({ notice: "Você foi impedido de ouvir e falar por um moderador da comunidade." });
          return;
        }
        const muted = !current.muted;
        patch({ muted });
        meshRef.current?.setMuted(muted);
        sock()?.emit("voice:state", { muted });
      },

      toggleDeafen: () => {
        const current = stateRef.current;
        if (current.deafened && current.me?.serverDeafened) {
          patch({ notice: "Você foi impedido de ouvir e falar por um moderador da comunidade." });
          return;
        }
        const deafened = !current.deafened;
        const muted = deafened ? true : current.muted;
        patch({ deafened, muted });
        meshRef.current?.setMuted(muted);
        sock()?.emit("voice:state", { deafened, muted });
      },

      toggleCam: () => {
        const mesh = ensureMesh();
        const next = !stateRef.current.cam;
        void mesh?.setCam(next).then(
          () => {
            patch({ cam: next });
            sock()?.emit("voice:state", { cam: next });
          },
          () => patch({ cam: false }),
        );
      },

      toggleShare: () => {
        const current = stateRef.current;
        if (current.sharing) {
          // Parar não precisa de confirmação: desliga direto.
          const mesh = meshRef.current;
          void mesh?.setScreen(false)?.then(() => {
            patch({ sharing: false, lastShareResult: null });
            sock()?.emit("voice:state", { sharing: false });
          });
          return;
        }
        if (!can(current, "tela")) {
          patch({ notice: "Você não tem permissão para transmitir a tela." });
          return;
        }
        // O passo seguinte (escolher qual tela/janela) é do navegador e não
        // dá para pular nem customizar — mas as opções antes dele (qualidade,
        // áudio do sistema) têm modal próprio.
        patch({ shareModalOpen: true });
      },

      closeShareModal: () => patch({ shareModalOpen: false }),

      confirmShare: () => {
        const current = stateRef.current;
        patch({ shareModalOpen: false });
        const mesh = ensureMesh();
        void mesh?.setScreen(true, current.shareSystemAudio).then(
          () => {
            patch({ sharing: true });
            sock()?.emit("voice:state", { sharing: true });
          },
          () => patch({ sharing: false }),
        );
      },

      setShareSystemAudio: on => patch({ shareSystemAudio: on }),

      setShareQuality: quality => {
        patch({ shareQuality: quality });
        void meshRef.current?.setShareQuality(quality);
      },

      setCamQuality: quality => {
        patch({ camQuality: quality });
        void meshRef.current?.setCamQuality(quality);
      },

      setContentHint: mode => {
        patch({ contentHint: mode });
        meshRef.current?.setContentHint(mode);
      },

      setNoiseSuppression: on => {
        patch({ noiseSuppression: on });
        meshRef.current?.setNoiseSuppressionPreference(on);
        // Troca a trilha em uso agora, reaproveitando o mesmo caminho que ja
        // existe para trocar de dispositivo de entrada (para o mic atual,
        // dispara "ended" sob a guarda que evita o aviso de "desconectado",
        // e reabre — desta vez lendo a preferencia nova).
        void restartMic();
      },

      setFocus: peerId => patch({ focus: peerId }),

      setVolume: (userId, volume) => {
        const clamped = Math.min(1, Math.max(0, volume));
        patch(prev => ({ volumes: { ...prev.volumes, [userId]: clamped } }));
      },

      openContextMenu: (key, userId, x, y) => patch({ contextMenu: { key, userId, x, y } }),
      closeContextMenu: () => patch({ contextMenu: null }),

      setPushToTalk: on => {
        const current = stateRef.current;
        // Ligado: o microfone fica fechado ate a barra de espaco abrir.
        const muted = on
          ? true
          : current.deafened ||
            Boolean(current.me?.serverMuted) ||
            Boolean(current.me?.serverDeafened) ||
            isTimedOut(current.me);
        patch({ pushToTalk: on, muted });
        meshRef.current?.setMuted(muted);
        if (current.voiceId && current.muted !== muted) {
          sock()?.emit("voice:state", { muted });
        }
      },

      setInputDevice: deviceId => {
        micDeviceRef.current = deviceId || null;
        patch(prev => ({ devices: { ...prev.devices, inputId: deviceId || null } }));
        void restartMic();
      },

      setOutputDevice: deviceId => {
        patch(prev => ({ devices: { ...prev.devices, outputId: deviceId || null } }));
      },

      toggleMembers: () => patch(prev => ({ membersOpen: !prev.membersOpen })),
      openProfile: userId => patch({ profile: userId, switcherOpen: false }),
      closeProfile: () => patch({ profile: null }),

      messageUser: userId => {
        patch({ profile: null });
        const me = stateRef.current.me;
        if (me) openKey(dmKey(me.id, userId), "diretas");
      },

      inviteToVoice: userId => {
        const current = stateRef.current;
        const me = current.me;
        if (!me || !current.voiceId) return;
        const sala = current.voiceChannels.find(room => room.id === current.voiceId);
        if (!sala) return;
        // Não existe chamada 1-a-1 no LFRCORD: a voz é por sala. Então
        // "chamar" é o que dá para fazer de verdade com o que existe —
        // avisar a pessoa, na direta, em que sala eu estou. Antes este item
        // do menu caía no `default` e respondia "essa ação ainda não
        // existe".
        const key = dmKey(me.id, userId);
        sock()?.emit("chat:send", {
          channelKey: key,
          body: `Te chamei para a sala **${sala.name}**.`,
          clientId: newId("m"),
          replyToId: null,
          attachments: [],
        });
        patch({ notice: "Convite enviado na direta." });
      },

      copyServerInvite: () => {
        const serverId = stateRef.current.activeServerId;
        if (!serverId) {
          patch({ notice: "Abra um servidor para pegar o convite dele." });
          return;
        }
        // O código chega pelo evento `servers:invite`; o `once` copia assim
        // que ele volta, para o clique não precisar de um segundo clique.
        const socket = sock();
        if (!socket) return;
        socket.once("servers:invite", ({ code }) => {
          void navigator.clipboard.writeText(code).then(
            () => patch({ notice: `Convite copiado: ${code}` }),
            () => patch({ notice: `Não consegui copiar. O código é ${code}` }),
          );
        });
        socket.emit("servers:invite", { serverId });
      },

      openProfileEditor: () => patch({ profileEditorOpen: true, profile: null }),
      closeProfileEditor: () => patch({ profileEditorOpen: false }),

      updateProfile: profilePatch => {
        // So aplica os campos realmente enviados: `undefined` nao apaga nada.
        const changes: Partial<User> = {};
        if (profilePatch.name !== undefined) changes.name = profilePatch.name;
        if (profilePatch.bio !== undefined) changes.bio = profilePatch.bio;
        if (profilePatch.color !== undefined) changes.color = profilePatch.color;
        if (profilePatch.avatarUrl !== undefined) changes.avatarUrl = profilePatch.avatarUrl;
        if (profilePatch.customStatus !== undefined) changes.customStatus = profilePatch.customStatus;
        patch(prev => {
          if (!prev.me) return {};
          const me: User = { ...prev.me, ...changes };
          return {
            me,
            users: prev.users.map(user => (user.id === me.id ? { ...user, ...changes } : user)),
          };
        });
        sock()?.emit("profile:update", profilePatch);
      },

      uploadImage: async file => {
        if (file.size > MAX_AVATAR_BYTES) {
          throw new Error(`A imagem precisa ter no máximo ${formatBytes(MAX_AVATAR_BYTES)}.`);
        }
        const attachment = await sendToServer(file, "avatar", () => undefined);
        return attachment.url;
      },

      openSettings: () => patch({ settingsOpen: true, switcherOpen: false }),
      closeSettings: () => patch({ settingsOpen: false, saved: false }),
      setSettingsTab: tab => patch({ settingsTab: tab }),
      selectRole: roleId => patch({ roleSel: roleId }),

      addRole: () => {
        const current = stateRef.current;
        const id = `cargo-${Date.now().toString(36)}`;
        const roles: Role[] = [
          ...current.roles,
          {
            id,
            name: "Novo cargo",
            color: "#9BAAD0",
            position: current.roles.length,
            perms: {
              canais: false,
              cargos: false,
              expulsar: false,
              silenciar: false,
              mensagens: true,
              voz: true,
              tela: false,
              todos: false,
            },
          },
        ];
        patch({ roles, roleSel: id });
        sock()?.emit("roles:update", { roles });
      },

      updateRole: (roleId, rolePatch) => {
        const roles = stateRef.current.roles.map(role =>
          role.id === roleId ? { ...role, ...rolePatch } : role,
        );
        patch({ roles });
        sock()?.emit("roles:update", { roles });
      },

      deleteRole: roleId => {
        if (roleId === OWNER_ROLE_ID) {
          patch({ notice: "O cargo de fundador não pode ser apagado." });
          return;
        }
        const current = stateRef.current;
        if (current.roles.length <= 1) {
          patch({ notice: "A comunidade precisa de pelo menos um cargo." });
          return;
        }
        const roles = current.roles
          .filter(role => role.id !== roleId)
          .map((role, index) => ({ ...role, position: index }));
        patch(prev => ({
          roles,
          roleSel: prev.roleSel === roleId ? (roles[0]?.id ?? "") : prev.roleSel,
        }));
        sock()?.emit("roles:update", { roles });
      },

      togglePermission: (roleId, perm) => {
        const roles = stateRef.current.roles.map(role =>
          role.id === roleId
            ? { ...role, perms: { ...role.perms, [perm]: !role.perms[perm] } }
            : role,
        );
        patch({ roles });
        sock()?.emit("roles:update", { roles });
      },

      setMemberRole: (userId, roleId) => {
        // Otimista, e ALTERNANDO: o clique num cargo que a pessoa ja tem tira,
        // e num que ela nao tem soma. Espelha o que o servidor faz, para a
        // tela nao mostrar por um instante algo diferente do que vai valer.
        patch(prev => {
          const alternar = (user: User): User => {
            if (user.id !== userId) return user;
            const tinha = user.roleIds.includes(roleId);
            const roleIds = tinha
              ? user.roleIds.filter(id => id !== roleId)
              : [...user.roleIds, roleId];
            if (roleIds.length === 0) return user; // o servidor recusa ficar sem nenhum
            const ordenados = [...roleIds].sort(
              (a, b) =>
                (prev.roles.find(r => r.id === a)?.position ?? 99) -
                (prev.roles.find(r => r.id === b)?.position ?? 99),
            );
            return { ...user, roleIds: ordenados, roleId: ordenados[0]! };
          };
          return {
            users: prev.users.map(alternar),
            // Mexer no proprio cargo tem que valer aqui tambem, pelo mesmo
            // motivo do evento la em cima: `me` e uma copia separada.
            me: prev.me ? alternar(prev.me) : prev.me,
          };
        });
        sock()?.emit("members:role", { userId, roleId });
      },

      kickMember: (userId, reason) => {
        const current = stateRef.current;
        if (current.me?.id === userId) {
          patch({ notice: "Você não pode expulsar a si mesmo." });
          return;
        }
        patch(prev => ({ profile: prev.profile === userId ? null : prev.profile }));
        sock()?.emit("members:kick", { userId, reason: reason ?? null });
      },

      banMember: (userId, reason) => {
        const current = stateRef.current;
        if (current.me?.id === userId) {
          patch({ notice: "Você não pode banir a si mesmo." });
          return;
        }
        patch(prev => ({ profile: prev.profile === userId ? null : prev.profile }));
        sock()?.emit("members:ban", { userId, reason: reason ?? null });
      },

      timeoutMember: (userId, untilMs, reason) => {
        sock()?.emit("members:timeout", { userId, untilMs, reason: reason ?? null });
      },

      setServerMute: (userId, serverMuted) => {
        patch(prev => ({
          users: prev.users.map(user => (user.id === userId ? { ...user, serverMuted } : user)),
        }));
        sock()?.emit("members:mute", { userId, serverMuted });
      },

      setServerDeafen: (userId, serverDeafened) => {
        patch(prev => ({
          users: prev.users.map(user => (user.id === userId ? { ...user, serverDeafened } : user)),
        }));
        sock()?.emit("members:deafen", { userId, serverDeafened });
      },

      disconnectFromVoice: userId => {
        sock()?.emit("members:voiceDisconnect", { userId });
      },

      moveMemberToVoice: (userId, voiceId) => {
        sock()?.emit("members:voiceMove", { userId, voiceId });
      },

      toggleBlockUser: userId => {
        const blocked = !stateRef.current.blocked.includes(userId);
        sock()?.emit("users:block", { userId, blocked });
      },

      toggleIgnoreUser: userId => {
        patch(prev => {
          const ignored = prev.ignored.includes(userId)
            ? prev.ignored.filter(id => id !== userId)
            : [...prev.ignored, userId];
          saveIgnoredUsers(ignored);
          return { ignored };
        });
      },

      requestAuditLog: before => {
        patch({ auditLogLoading: true });
        sock()?.emit("admin:auditLog", { before });
      },

      createChannel: (kind, name, topic) => {
        const trimmed = name.trim();
        if (!trimmed) {
          patch({ notice: "Dê um nome ao canal." });
          return;
        }
        sock()?.emit("channels:create", { kind, name: trimmed, topic });
      },

      updateChannel: (kind, id, channelPatch) => {
        sock()?.emit("channels:update", { kind, id, ...channelPatch });
      },

      deleteChannel: (kind, id) => {
        sock()?.emit("channels:delete", { kind, id });
      },

      updateSettings: settingsPatch =>
        patch(prev => ({ settings: { ...prev.settings, ...settingsPatch } })),

      saveSettings: settingsPatch => {
        patch(prev => ({ settings: { ...prev.settings, ...settingsPatch }, saved: true }));
        sock()?.emit("settings:update", settingsPatch);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => patch({ saved: false }), 2_200);
      },

      setStatus: status => {
        // Escolha do membro manda: desliga o ausente automatico.
        autoAwayRef.current = false;
        lastActivityRef.current = Date.now();
        patch(prev => ({
          autoAway: false,
          me: prev.me ? { ...prev.me, status } : prev.me,
          users: prev.users.map(user => (user.id === prev.me?.id ? { ...user, status } : user)),
        }));
        sock()?.emit("presence:status", { status });
      },

      dismissNotice: () => patch({ notice: null }),
      setNotice: message => patch({ notice: message }),

      createServer: name => {
        const limpo = name.trim();
        if (limpo) sock()?.emit("servers:create", { name: limpo });
      },
      joinServer: code => {
        const limpo = code.trim().toUpperCase();
        if (limpo) sock()?.emit("servers:join", { code: limpo });
      },
      leaveServer: serverId => {
        sock()?.emit("servers:leave", { serverId });
      },
      deleteServer: serverId => {
        sock()?.emit("servers:delete", { serverId });
      },
      selectServer: serverId => {
        if (serverId === stateRef.current.activeServerId) return;
        // O código do servidor anterior não vale para o novo.
        patch({ inviteCode: null });
        sock()?.emit("servers:select", { serverId });
      },
      requestInvite: serverId => {
        sock()?.emit("servers:invite", { serverId });
      },

      addFriend: target => {
        const handle = target.handle?.trim();
        if (target.userId) sock()?.emit("friends:request", { userId: target.userId });
        else if (handle) sock()?.emit("friends:request", { handle: normalizeHandle(handle) });
      },
      acceptFriend: targetId => {
        sock()?.emit("friends:accept", { userId: targetId });
      },
      removeFriend: targetId => {
        sock()?.emit("friends:remove", { userId: targetId });
      },

      logout: () => {
        if (kickTimer.current) {
          clearTimeout(kickTimer.current);
          kickTimer.current = null;
        }
        // Avisa o servidor antes de derrubar o socket, para o token deixar de
        // valer de verdade em vez de so sumir desta maquina. Sem `await`: sair
        // da conta nao pode ficar esperando a rede, e `keepalive` garante que
        // o pedido saia mesmo se a aba fechar em seguida.
        void endSessionOnServer();
        meshRef.current?.destroy();
        meshRef.current = null;
        socketRef.current?.disconnect();
        logoutRef.current();
      },

      applySession: token => {
        applyTokenRef.current(token);
      },

      openSwitcher: () => patch({ switcherOpen: true, reactionPickerFor: null }),
      closeSwitcher: () => patch({ switcherOpen: false }),

      gotoTarget: target => {
        if (target.kind === "canal") {
          openKey(makeChannelKey(target.id), "canais");
          return;
        }
        if (target.kind === "voz") {
          patch({ activeKey: `voice:${target.id}`, focus: null, switcherOpen: false });
          return;
        }
        const me = stateRef.current.me;
        if (!me || me.id === target.id) {
          patch({ switcherOpen: false });
          return;
        }
        openKey(dmKey(me.id, target.id), "diretas");
      },

      setNotificationsEnabled: enabled => {
        const prefs = savePrefs({ enabled });
        // O pedido de permissao sai deste clique, nunca do carregamento.
        if (enabled && prefs.permission === "default") askPermission();
      },

      setNotificationSound: sound => {
        savePrefs({ sound });
        // O clique tambem serve de gesto para o navegador liberar o audio.
        if (sound) ensureAudio(audioRef);
      },

      requestNotificationPermission: askPermission,
    };
  }, [
    askPermission,
    ensureMesh,
    openKey,
    patch,
    refreshDevices,
    restartMic,
    savePrefs,
    sendToServer,
    updateUpload,
  ]);

  const value = useMemo<AppContextValue>(() => ({ state, actions }), [state, actions]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/** Nome do evento → campo do ouvinte. */
const ADMIN_CHAVE = {
  "admin:overview": "overview",
  "admin:users": "usuarios",
  "admin:user": "usuario",
  "admin:servers": "servidores",
  "admin:server": "servidor",
  "admin:audit": "auditoria",
  "admin:flags": "flags",
  "admin:ok": "ok",
  "admin:banidos": "banidos",
  "admin:sessoesFundadores": "sessoes",
  "admin:evento": "evento",
  "admin:crescimento": "crescimento",
} as const satisfies Record<string, keyof AdminOuvintes>;

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp precisa estar dentro de <AppProvider>.");
  return value;
}

/* -------------------------------------------------------------------- */
/* Seletores                                                             */
/* -------------------------------------------------------------------- */

export function userById(state: AppState, id: string): User | undefined {
  return state.users.find(user => user.id === id);
}

export function roleOf(state: AppState, userId: string): Role | undefined {
  const user = userById(state, userId);
  if (!user) return undefined;
  const daqui = state.roles.find(role => role.id === user.roleId);
  if (daqui) return daqui;
  // Nao achou: o recuo tem que ser o cargo MENOS graduado, nao o primeiro da
  // lista. Cair no topo fazia quem nao tinha cargo neste servidor aparecer
  // como Dono — e era o caso comum, porque o cargo que chegava aqui era o da
  // conta e nao o da participacao.
  return state.roles.length > 0 ? state.roles[state.roles.length - 1] : undefined;
}

/** Cor efetiva do membro: a propria, ou o acento da comunidade. */
export function colorOf(state: AppState, userId: string): string {
  return userById(state, userId)?.color ?? state.settings.accent;
}

export function messagesOf(state: AppState, key: string): Message[] {
  const list = state.messages[key] ?? [];
  if (state.blocked.length === 0 && state.ignored.length === 0) return list;
  // Bloqueado ou ignorado: some da conversa pra quem bloqueou/ignorou, em
  // canal ou DM — a mensagem continua existindo pros outros, só não aparece
  // pra este membro.
  return list.filter(
    message => !state.blocked.includes(message.authorId) && !state.ignored.includes(message.authorId),
  );
}

/** Procura uma mensagem por id em todas as conversas ja carregadas. */
export function messageById(state: AppState, messageId: string): Message | undefined {
  for (const list of Object.values(state.messages)) {
    const found = list.find(message => message.id === messageId);
    if (found) return found;
  }
  return state.search.results.find(message => message.id === messageId);
}

/**
 * A permissão efetiva de alguém: a UNIÃO dos cargos que a pessoa tem AQUI.
 *
 * `roleOf` devolve só o mais alto, porque é ele que dá a cor do nome e agrupa
 * o painel de membros. Permissão é outra pergunta: um cargo nunca anula o
 * outro, e o servidor sempre decidiu assim (`members:role` em `realtime.ts`
 * calcula a união dos cargos do ator). A tela é que não calculava.
 *
 * A consequência era exatamente o sintoma de "dei o cargo e não mudou nada":
 * um cargo dado ABAIXO do que a pessoa já tinha não vira `roleId`, então a
 * tela seguia lendo as permissões do cargo antigo e nenhum botão novo
 * aparecia — enquanto o servidor já aceitava a ação. Só que o botão para
 * disparar essa ação não estava mais na tela.
 */
export function permsOf(state: AppState, userId: string): Partial<Record<PermissionId, boolean>> {
  const user = userById(state, userId);
  if (!user) return {};
  const ids = user.roleIds.length > 0 ? user.roleIds : [user.roleId];
  const uniao: Partial<Record<PermissionId, boolean>> = {};
  let achou = false;
  for (const id of ids) {
    const role = state.roles.find(item => item.id === id);
    if (!role) continue;
    achou = true;
    for (const [perm, ligada] of Object.entries(role.perms)) {
      if (ligada) uniao[perm as PermissionId] = true;
    }
  }
  // Nenhum cargo resolveu: cai no mesmo recuo do `roleOf` — o cargo MENOS
  // graduado da lista, nunca o primeiro.
  if (!achou) return roleOf(state, userId)?.perms ?? {};
  return uniao;
}

export function can(state: AppState, perm: PermissionId): boolean {
  if (!state.me) return false;
  // Fundador da plataforma passa em tudo, em qualquer servidor. O servidor
  // decide igual; isto só impede a tela de esconder o controle de quem tem
  // o poder mas não consta na lista de cargos daquele lugar.
  if (state.platformFounder) return true;
  return Boolean(permsOf(state, state.me.id)[perm]);
}

/** Participantes conectados a uma sala de voz, na ordem de entrada. */
export function participantsOf(state: AppState, voiceId: string): VoiceState[] {
  return state.voiceStates
    .filter(item => item.voiceId === voiceId)
    .sort((a, b) => a.joinedAt - b.joinedAt);
}

/** Conversas diretas: todos os outros membros, com a ultima mensagem trocada. */
/**
 * Alguem com quem da para conversar. Nao e o `User` inteiro de proposito: a
 * pessoa pode ser um amigo de fora da comunidade, de quem so conhecemos o
 * perfil minimo que o servidor mandou junto das relacoes.
 */
export type DmPerson = Pick<User, "id" | "name" | "handle" | "avatarUrl" | "color" | "status"> & {
  customStatus: string | null;
};

export type DmEntry = { user: DmPerson; key: string; last: Message | null };

/** O perfil mais rico que temos: o membro completo, ou o recorte das amizades. */
function dmPersonOf(state: AppState, userId: string): DmPerson | null {
  const full = state.users.find(user => user.id === userId);
  if (full) return full;

  const profile = state.friendProfiles[userId];
  if (!profile) return null;
  return {
    id: profile.id,
    name: profile.name,
    handle: profile.handle,
    avatarUrl: profile.avatarUrl,
    color: profile.color,
    // Presenca de quem esta fora da comunidade ainda nao chega ate aqui — e
    // parte do escopo por servidor. Ate la, "offline" e a resposta honesta:
    // melhor nao afirmar nada do que inventar que a pessoa esta online.
    status: "offline",
    customStatus: null,
  };
}

/**
 * A barra de conversas diretas.
 *
 * Antes isto era `state.users` menos eu — ou seja, a barra de diretas ERA o
 * catalogo de todas as contas da plataforma, e qualquer cadastro novo
 * aparecia para todo mundo. Agora sai de quem tem relacao de verdade:
 * amizade aceita, ou conversa que ja existe.
 *
 * Solicitacao pendente NAO entra: ainda nao e uma conversa, e mostrar como se
 * fosse convidaria a mandar mensagem que o servidor vai recusar.
 */
export function dmEntries(state: AppState): DmEntry[] {
  const me = state.me;
  if (!me) return [];

  // Sem a migracao de amizades aplicada, o servidor nao tem como dizer quem e
  // amigo de quem — e o portao de DM tambem esta desligado do outro lado.
  // Voltar ao comportamento antigo mantem o app coerente consigo mesmo.
  const ids = state.friendsAvailable
    ? new Set([
        ...state.friends.filter(link => link.state === "amigo").map(link => link.userId),
        ...state.conversations,
      ])
    : new Set(state.users.filter(user => user.id !== me.id).map(user => user.id));

  const entries: DmEntry[] = [];
  for (const id of ids) {
    if (id === me.id) continue;
    const user = dmPersonOf(state, id);
    if (!user) continue;
    const key = dmKey(me.id, id);
    const list = state.messages[key] ?? [];
    entries.push({ user, key, last: list[list.length - 1] ?? null });
  }

  return entries.sort((a, b) => {
    const byRecency = (b.last?.createdAt ?? 0) - (a.last?.createdAt ?? 0);
    // Sem mensagem nenhuma dos dois lados, ordem alfabetica — senao a lista
    // de amigos novos embaralharia a cada render.
    return byRecency !== 0 ? byRecency : a.user.name.localeCompare(b.user.name, "pt-BR");
  });
}

/**
 * Membros do servidor ABERTO, em ordem de nome.
 *
 * `state.users` nao serve para isto: la mora todo mundo que eu enxergo, o que
 * inclui amigos de fora deste servidor. Antes da migracao multi-servidor a
 * lista nao vinha do servidor, entao aqui ha o recuo para `users` — instalacao
 * sem servidor nenhum continua mostrando a comunidade inteira, como sempre fez.
 */
export function serverMembers(state: AppState): User[] {
  if (!state.serversAvailable || state.activeServerId === null) {
    return [...state.users].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }
  const daqui = new Set(state.serverMemberIds);
  return state.users
    .filter(user => daqui.has(user.id))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

/** Como estou em relacao a esta pessoa: amigo, pedido recebido, enviado, ou nada. */
export function friendState(state: AppState, userId: string): FriendState | null {
  return state.friends.find(link => link.userId === userId)?.state ?? null;
}

/** Solicitacoes que chegaram e ainda esperam resposta. */
export function pendingFriendRequests(state: AppState): FriendLink[] {
  return state.friends.filter(link => link.state === "recebida");
}

/** Solicitacoes que eu mandei e ainda nao foram respondidas. */
export function sentFriendRequests(state: AppState): FriendLink[] {
  return state.friends.filter(link => link.state === "enviada");
}

export function activeChannel(state: AppState): TextChannel | undefined {
  if (!state.activeKey.startsWith("ch:")) return undefined;
  const id = state.activeKey.slice(3);
  return state.channels.find(channel => channel.id === id);
}

export function activeDmUser(state: AppState): User | undefined {
  if (!state.me) return undefined;
  const partner = dmPartner(state.activeKey, state.me.id);
  return partner ? userById(state, partner) : undefined;
}

export function activeVoiceChannel(state: AppState): VoiceChannel | undefined {
  if (!state.activeKey.startsWith("voice:")) return undefined;
  const id = state.activeKey.slice(6);
  return state.voiceChannels.find(channel => channel.id === id);
}

export function connectedVoiceChannel(state: AppState): VoiceChannel | undefined {
  return state.voiceChannels.find(channel => channel.id === state.voiceId);
}

export function onlineCount(state: AppState): number {
  return state.users.filter(user => user.status === "online").length;
}

/** Nomes de quem esta digitando na conversa, sem contar voce. */
export function typingIn(state: AppState, key: string): string[] {
  const cutoff = Date.now() - TYPING_TTL_MS;
  const names: string[] = [];
  for (const entry of state.typing[key] ?? []) {
    if (entry.at <= cutoff) continue;
    if (state.me && entry.userId === state.me.id) continue;
    const name = userById(state, entry.userId)?.name;
    if (name) names.push(name);
  }
  return names;
}

export function unreadOf(state: AppState, key: string): number {
  return state.unread[key]?.count ?? 0;
}

export function mentionsOf(state: AppState, key: string): number {
  return state.unread[key]?.mentions ?? 0;
}

/**
 * Hora da ultima mensagem ja lida na conversa: a linha "novas mensagens" entra
 * antes da primeira com `createdAt` maior. `null` quando nao ha linha.
 */
export function newSinceOf(state: AppState, key: string): number | null {
  const value = state.newSince[key];
  return typeof value === "number" ? value : null;
}

/** Membros de um cargo, em ordem alfabetica. */
export function roleMembers(state: AppState, roleId: string): User[] {
  // Só membros DESTE servidor: um cargo pertence a um servidor, e listar
  // gente de fora nele expunha o cadastro para quem abrisse a aba Cargos.
  return serverMembers(state)
    .filter(user => user.roleId === roleId)
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}
